import dayjs from "dayjs";
import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import type { Translate } from "@similarweb/i18n-provider";
import type {
    MonitorAndProtectModelSharedRequestDeviceType,
    MonitorAndProtectModelTrademarksAdsResponseTrademarkAdScrapesTableRow,
    MonitorAndProtectModelTrademarksAdsViolationType,
} from "@similarweb/pro-api-client";
import type { ITableProps } from "@similarweb/pro-logic-components";
import { TextLink } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import type {
    IActionModalCell,
    IAdCell,
    IBadgeCell,
    IDeviceCell,
    IDomainCell,
    ITextCell,
} from "app/pages/digital-marketing/monitor-and-protect/shared/ui/table/cells";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { NumberFormatter } from "../../../services/number-formatter";
import type { TableRow } from "../../../shared/ui/table/builders";
import { ColumnBuilder, ConfigBuilder, RowBuilder } from "../../../shared/ui/table/builders";
import type { ICountryCell } from "../../../shared/ui/table/cells/components/country-cell";

export type IExampleTableRow = TableRow<{
    title: IAdCell;
    violationType: IBadgeCell;
    keyword: ITextCell;
    advertiser: IDomainCell;
    firstSeen: ITextCell;
    lastSeen: ITextCell;
    timesSeen: ITextCell;
    avgRank: ITextCell;
    device: IDeviceCell;
    country: ICountryCell;
    googleUrl: IActionModalCell;
}>;

const TextLinkStyled = styled(TextLink)`
    && {
        outline: none;
    }
`;

const createParentColumnBuilder = (pagination: ITableProps["pagination"] | undefined, translateFn: Translate) => {
    return new ColumnBuilder<IExampleTableRow>()
        .addRowNumberColumn("rowNum", {
            title: "",
            currentPage: pagination?.current,
            pageSize: pagination?.pageSize,
            fixed: "left",
        })
        .addColumn("title", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.ad_content", {
                recordsCount: NumberFormatter.toAbbr(pagination?.total ?? 0),
            }),
            sorter: true,
            tooltipSubtitle: translateFn("monitor_and_protect.page.trademark.table.columns.ad_content.tooltip"),
            width: 450,
            fixed: "left",
        })
        .addColumn("keyword", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.keyword"),
            sorter: true,
            tooltipSubtitle: translateFn("monitor_and_protect.page.trademark.table.columns.keyword.tooltip"),
            align: "right",
            width: 200,
        })
        .addColumn("violationType", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.violation_type"),
            width: 125,
            sorter: true,
            align: "left",
            tooltipSubtitle: translateFn("monitor_and_protect.page.trademark.table.columns.violationType.tooltip"),
        })
        .addColumn("timesSeen", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.times_seen"),
            tooltipSubtitle: translateFn("monitor_and_protect.page.trademark.table.columns.times_seen.tooltip"),
            sorter: true,
            align: "right",
            width: 110,
        })
        .addColumn("advertiser", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.domain"),
            tooltipSubtitle: translateFn("monitor_and_protect.page.trademark.table.columns.domain.tooltip"),
            sorter: true,
            width: 150,
        })
        .addColumn("firstSeen", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.first_seen"),
            sorter: true,
            align: "right",
            width: 110,
            hidden: true,
        })
        .addColumn("lastSeen", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.last_seen"),
            sorter: true,
            align: "right",
            width: 110,
            hidden: true,
        })
        .addColumn("avgRank", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.avg_rank"),
            tooltipSubtitle: translateFn("monitor_and_protect.page.trademark.table.columns.avg_rank.tooltip"),
            width: 80,
        })
        .addColumn("googleUrl", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.tracking_url"),
            width: 130,
            hidden: true,
        })
        .addColumn("device", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.device"),
            tooltipSubtitle: translateFn("monitor_and_protect.page.trademark.table.columns.device.tooltip"),
            align: "center",
            width: 60,
        })
        .addColumn("country", {
            title: translateFn("monitor_and_protect.page.trademark.table.columns.country"),
            tooltipSubtitle: translateFn("monitor_and_protect.page.trademark.table.columns.country.tooltip"),
            align: "left",
            width: 140,
        });
};

const deviceMap: { [key in MonitorAndProtectModelSharedRequestDeviceType]: IDeviceCell["device"] } = {
    Desktop: "desktop",
    MobileWeb: "mobile",
    All: "both",
};

const ViolationTypePopoverContent = (props: { violationType: MonitorAndProtectModelTrademarksAdsViolationType }) => {
    const { translate } = useTranslate();
    const isAdCopyViolation = props.violationType === "AdCopy";

    return (
        <Stack
            gap="4px"
            width="185px"
        >
            <Text fontVariant="listItem4">
                {translate(
                    isAdCopyViolation
                        ? "monitor_and_protect.page.trademark.table.violation_type_popover.title"
                        : "monitor_and_protect.page.trademark.table.violation_type_keyword_popover.title",
                )}
            </Text>
            <Text fontVariant="tooltip">
                {translate(
                    isAdCopyViolation
                        ? "monitor_and_protect.page.trademark.table.violation_type_popover.description"
                        : "monitor_and_protect.page.trademark.table.violation_type_keyword_popover.description",
                )}
            </Text>
            {isAdCopyViolation ? (
                <TextLinkStyled
                    href="https://support.google.com/legal/answer/3110420"
                    autoFocus={false}
                    target="_blank"
                    translations={{
                        opensInNewTab: translate(
                            "monitor_and_protect.page.trademark.table.violation_type_popover.learn_more",
                        ),
                    }}
                >
                    {translate("monitor_and_protect.page.trademark.table.violation_type_popover.learn_more")}
                </TextLinkStyled>
            ) : null}
        </Stack>
    );
};

const ViolationTypeTranslationKeys: Record<MonitorAndProtectModelTrademarksAdsViolationType, string> = {
    Keyword: "monitor_and_protect.features.trademark_dashboard.filters.violation_type_keyword",
    AdCopy: "monitor_and_protect.features.trademark_dashboard.filters.violation_type_ad_copy",
    Unknown: "monitor_and_protect.features.trademark_dashboard.filters.violation_type_unknown",
};

export const getParentRowBuilder = (data: {
    pagination: ITableProps["pagination"] | undefined;
    translateFn: Translate;
    advertiserHoverActions?: IDomainCell["hoverActions"];
}) => {
    return new RowBuilder<MonitorAndProtectModelTrademarksAdsResponseTrademarkAdScrapesTableRow, IExampleTableRow>(
        (e) => e.adId,
        createParentColumnBuilder(data.pagination, data.translateFn),
    )
        .mapField("title", (e) => ({
            type: "ad",
            title: e.title ?? "",
            iconUrl: getFaviconUrl(e.advertiser),
            description: e.description ?? "",
            domain: e.advertiser,
            data: e.urlShown ?? "",
            isPla: e.adType === "pla",
            imageUrl: e.image ?? "",
            price: e.price ?? "",
            adUrl: e.url,
        }))
        .mapField("violationType", (e) => ({
            type: "badge",
            text: data.translateFn(
                ViolationTypeTranslationKeys[e.violationType] ??
                    "monitor_and_protect.features.trademark_dashboard.filters.violation_type_unknown",
            ),
            badgeType: "new",
            data: e.violationType,
            popoverContent: <ViolationTypePopoverContent violationType={e.violationType} />,
        }))
        .mapField("keyword", (e) => ({
            type: "text",
            text: e.keyword,
            tooltip: e.keyword,
            data: e.keyword,
        }))
        .mapField("advertiser", (e) => ({
            type: "domain",
            data: e.advertiser,
            domainUrl: e.advertiser,
            iconUrl: getFaviconUrl(e.advertiser),
            title: e.advertiser,
            hoverActions: data.advertiserHoverActions,
        }))
        .mapField("firstSeen", (e) => ({
            type: "text",
            text: e.firstSeen ? dayjs(e.firstSeen).format("DD MMM") : "-",
            data: e.firstSeen,
        }))
        .mapField("lastSeen", (e) => ({
            type: "text",
            text: e.lastSeen ? dayjs(e.lastSeen).format("DD MMM") : "-",
            data: e.lastSeen,
        }))
        .mapField("timesSeen", (e) => ({
            type: "text",
            text: e.timesSeen?.toLocaleString() || "-",
            data: e.timesSeen,
        }))
        .mapField("avgRank", (e) => ({
            type: "text",
            text: e.avgRank?.toFixed(2) ?? "-",
            data: e.avgRank,
        }))
        .mapField("googleUrl", (e) => ({
            type: "actionModal",
            text: data.translateFn("monitor_and_protect.page.trademark.table.columns.view_url"),
            content: e.googleUrl ?? "",
            data: e.googleUrl ?? "",
        }))
        .mapField("device", (e) => ({
            type: "device",
            device: deviceMap[e.device] || "both",
            data: e.device,
        }))
        .mapField("country", (e) => ({
            type: "country",
            countryCode: e.country,
            includeCountryName: true,
            data: e.country,
        }));
};

export const createConfigBuilder = (pagination: ITableProps["pagination"] | undefined, translateFn: Translate) => {
    return new ConfigBuilder(createParentColumnBuilder(pagination, translateFn));
};
